import { useTranslation } from 'react-i18next';
import { ArrowLeft, ListFilter } from 'lucide-react';
import type { FC } from 'react';
import { Button } from '@ui/button.tsx';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import type { ReferenceReferenceReadModel } from '@r/api/generated/client.schemas.ts';
import type { BoosterReferenceBoosterWriteModel } from '@r/api/generated/client.schemas.ts';
import type { BoosterDraftData } from './create-booster-form-schema.ts';
import { BoosterReferenceCoefficientDialog } from './booster-reference-coefficient-dialog.tsx';
import type { BoosterReferencesSelectionState } from './booster-references-selection.ts';
import { useSelectBoosterReferencesStep } from './use-select-booster-references-step.ts';

const EMPTY_INITIAL_REFERENCES: ReferenceReferenceReadModel[] = [];
const EMPTY_ORIGINAL_BOOSTER_REFERENCES: BoosterReferenceBoosterWriteModel[] = [];

interface SelectBoosterReferencesStepProps {
  draft: BoosterDraftData;
  onBack: () => void;
  onSuccess: () => void;
  referencesSelection: BoosterReferencesSelectionState;
  onReferencesSelectionChange: (state: BoosterReferencesSelectionState) => void;
  mode?: 'create' | 'edit';
  boosterId?: number;
  originalDraft?: BoosterDraftData;
  /** Références déjà rattachées au booster (édition) : toujours disponibles à la sélection. */
  pinnedReferences?: ReferenceReferenceReadModel[];
  originalBoosterReferences?: BoosterReferenceBoosterWriteModel[];
}

/**
 * Étape 2 (création/édition) : tableau des références avec celles en conflit grisées.
 * La disponibilité est calculée côté API via `available[startDate]`/`available[endDate]`.
 */
export const SelectBoosterReferencesStep: FC<SelectBoosterReferencesStepProps> = ({
  draft,
  onBack,
  onSuccess,
  referencesSelection,
  onReferencesSelectionChange,
  mode = 'create',
  boosterId,
  originalDraft,
  pinnedReferences = EMPTY_INITIAL_REFERENCES,
  originalBoosterReferences = EMPTY_ORIGINAL_BOOSTER_REFERENCES
}) => {
  const { t } = useTranslation();

  const {
    isEditMode,
    showOnlySelected,
    setShowOnlySelected,
    coefficientDialogReference,
    setCoefficientDialogReference,
    selectedReferenceIds,
    references,
    table,
    filterFields,
    handleSelectAll,
    handleSubmit,
    handleSaveReferenceTiers,
    coefficientDialogInitialTiers,
    isSubmitting,
    isLoading,
    hasSelectableReferences
  } = useSelectBoosterReferencesStep({
    draft,
    onSuccess,
    referencesSelection,
    onReferencesSelectionChange,
    mode,
    boosterId,
    originalDraft,
    pinnedReferences,
    originalBoosterReferences
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 justify-between items-center">
        <p className="text-sm font-medium">
          {t('boosters.create.references.selection.label')}{' '}
          <span className="font-normal text-muted-foreground">
            {t('boosters.create.references.selected', { count: selectedReferenceIds.size })}
          </span>
        </p>

        <div className="flex flex-wrap gap-2 items-center">
          <Button
            type="button"
            variant="outline"
            rounded="full"
            size="sm"
            onClick={() => setShowOnlySelected((value) => !value)}
          >
            <ListFilter className="mr-2 w-4 h-4" />
            {showOnlySelected
              ? t('boosters.create.references.show.all')
              : t('boosters.create.references.show.selected')}
          </Button>
          <Button
            type="button"
            variant="outline"
            rounded="full"
            size="sm"
            onClick={handleSelectAll}
            disabled={!hasSelectableReferences}
          >
            {t('boosters.create.references.select.all')}
          </Button>
        </div>
      </div>

      {isLoading && (
        <DataTableSkeleton
          columnCount={8}
          cellWidths={['3rem', '8rem', '8rem', '12rem', '10rem', '8rem', '10rem', '12rem']}
        />
      )}

      {!isLoading && references.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('boosters.create.references.empty')}</p>
      )}

      {!isLoading && references.length > 0 && (
        <>
          <DataTableAdvancedToolbar table={table} filterFields={filterFields} />
          <DataTable table={table} />
        </>
      )}

      <div className="flex flex-col gap-3 pt-2 sm:flex-row">
        <Button type="button" variant="outline" rounded="full" onClick={onBack}>
          <ArrowLeft className="mr-2 w-4 h-4" />
          {t('common.back')}
        </Button>
        <Button
          type="button"
          rounded="full"
          className="flex-1"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {isEditMode ? t('boosters.edit.confirm') : t('boosters.create.confirm')}
        </Button>
      </div>

      <BoosterReferenceCoefficientDialog
        key={coefficientDialogReference?.id ?? 'closed'}
        isOpen={coefficientDialogReference != null}
        onOpenChange={(open) => {
          if (!open) {
            setCoefficientDialogReference(null);
          }
        }}
        reference={coefficientDialogReference}
        globalCoefficient={draft.coefficient}
        initialTiers={coefficientDialogInitialTiers}
        onSave={handleSaveReferenceTiers}
      />
    </div>
  );
};
